// src/components/ResumeBuilder/forms/WorkExperienceForm.jsx
import { useContext } from "react";
import {
  Box,
  Card,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { FieldArray, Formik, Form } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GradientButton from "../Button";
import { ResumeContext } from "../../ResumeContext";
import { saveResumeData } from "../../api/resumeapi";
import BasicInfoSkeleton from "./skeletonLoader";
import { WorkExperienceSchema } from "./resumevalidation";
import dayjs from "dayjs";


export default function WorkExperienceForm({ onNext }) {
  const { data, setData, loading } = useContext(ResumeContext);

  //  Skeleton while loading
  if (loading || !data) {
    return <BasicInfoSkeleton />;
  }

  return (
    <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
      <Typography fontSize={13} color="#6E6D81" mb={2}>
        The biggest source of knowledge is experience, share your work experience!
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Formik
          enableReinitialize
          initialValues={{
            experiences:
              Array.isArray(data?.workArray) && data.workArray.length > 0
                ? data.workArray.map((exp) => ({
                  company: exp.company || "",
                  designation: exp.designation || "",
                  employmentType: exp.employmentType || "",
                  startDate: exp.start
                    ? dayjs(exp.start, "DD/MM/YYYY")
                    : null,
                  endDate:
                    exp.end && exp.end !== "current"
                      ? dayjs(exp.end, "DD/MM/YYYY")
                      : null,
                  currentlyWorking: exp.end === "current",
                  description: exp.description || "",
                }))
                : [
                  {
                    company: "",
                    designation: "",
                    employmentType: "",
                    startDate: null,
                    endDate: null,
                    currentlyWorking: false,
                    description: "",
                  },
                ],
          }}

          validationSchema={WorkExperienceSchema}
          onSubmit={async (values) => {
            const workArray = values.experiences.map((exp) => ({
              company: exp.company,
              designation: exp.designation,
              employmentType: exp.employmentType,
              start: exp.startDate
                ? exp.startDate.format("DD/MM/YYYY")
                : "",
              end: exp.currentlyWorking
                ? "current"
                : exp.endDate
                  ? exp.endDate.format("DD/MM/YYYY")
                  : "",
              description: exp.description,
            }));

            const payload = {
              ...data,
              workArray,
            };

            try {
              // SAVE TO DB
              console.log("Saving Work Experience:", values.experiences, payload);
              await saveResumeData(payload);

              // UPDATE CONTEXT
              setData((prev) => ({
                ...prev,
                workArray,
              }));
            } catch (err) {
              console.error("WORK EXPERIENCE SAVE ERROR:", err);
            }
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <Form>
              <FieldArray name="experiences">
                {({ push, remove }) => (
                  <Box>
                    {values.experiences.map((exp, idx) => (
                      <Box
                        key={idx}
                        sx={{ mb: 3, borderBottom: "1px dashed #eee", pb: 3 }}
                      >
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              label="Company"
                              name={`experiences[${idx}].company`}
                              size="small"
                              value={exp.company}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              label="Designation"
                              name={`experiences[${idx}].designation`}
                              size="small"
                              value={exp.designation}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              select
                              label="Employment Type"
                              name={`experiences[${idx}].employmentType`}
                              size="small"
                              value={exp.employmentType}
                              onChange={handleChange}
                            >
                              <MenuItem value="Full-time">Full-time</MenuItem>
                              <MenuItem value="Part-time">Part-time</MenuItem>
                              <MenuItem value="Contract">Contract</MenuItem>
                              <MenuItem value="Internship">Internship</MenuItem>
                              <MenuItem value="Freelance">Freelance</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <DatePicker
                              label="Start Date"
                              value={exp.startDate}
                              onChange={(date) =>
                                setFieldValue(
                                  `experiences[${idx}].startDate`,
                                  date
                                )
                              }
                              slotProps={{
                                textField: { size: "small", fullWidth: true },
                              }}
                            />
                          </Grid>

                          {!exp.currentlyWorking && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <DatePicker
                                label="End Date"
                                value={exp.endDate}
                                onChange={(date) =>
                                  setFieldValue(
                                    `experiences[${idx}].endDate`,
                                    date
                                  )
                                }
                                slotProps={{
                                  textField: { size: "small", fullWidth: true },
                                }}
                              />
                            </Grid>
                          )}

                          <Grid size={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={exp.currentlyWorking}
                                  onChange={(e) => {
                                    setFieldValue(
                                      `experiences[${idx}].currentlyWorking`,
                                      e.target.checked
                                    );
                                    if (e.target.checked) {
                                      setFieldValue(
                                        `experiences[${idx}].endDate`,
                                        null
                                      );
                                    }
                                  }}
                                />
                              }
                              label="I am currently working here"
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Description"
                              name={`experiences[${idx}].description`}
                              size="small"
                              value={exp.description}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={12} textAlign="right">
                            <IconButton
                              onClick={() => remove(idx)}
                              disabled={values.experiences.length === 1}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        push({
                          company: "",
                          designation: "",
                          employmentType: "",
                          startDate: null,
                          endDate: null,
                          currentlyWorking: false,
                          description: "",
                        })
                      }
                    >
                      Add experience
                    </Button>
                  </Box>
                )}
              </FieldArray>

              {/* BUTTONS */}
              <Box mt={3} display="flex" justifyContent="center" gap={4}>
                <GradientButton
                  type="button"
                  onClick={onNext}
                >
                  Next
                </GradientButton>

                <GradientButton type="submit">
                  Save
                </GradientButton>
              </Box>
            </Form>
          )}
        </Formik>
      </LocalizationProvider>
    </Card>
  );
}
